import {
    collection,
    doc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    orderBy
} from "firebase/firestore";
import { limitedTierlistDb } from "../config";

export const getAllSets = async () => {
    try {
        const setsRef = collection(limitedTierlistDb, "sets");
        const q = query(setsRef, orderBy("sort_number"));
        const querySnapshot = await getDocs(q);

        const sets = querySnapshot.docs.map(setDoc => ({
            id: setDoc.id,
            ...setDoc.data()
        }));

        return sets;
    } catch (error) {
        console.error("Błąd podczas pobierania setów:", error);
        throw error;
    }
};

export const updateSet = async (setId, updateData) => {
    try {
        if (!setId) {
            throw new Error(`Brak ID setu: ${setId}`);
        }

        const setIdStr = String(setId);
        console.log(`Próba aktualizacji setu: ${setIdStr} (oryginalne: ${setId}, typ: ${typeof setId}) z danymi:`, updateData);

        const setRef = doc(limitedTierlistDb, "sets", setIdStr);
        await updateDoc(setRef, updateData);
        console.log(`Pomyślnie zaktualizowano set ${setIdStr}`);
    } catch (error) {
        console.error("Błąd podczas aktualizacji setu:", error);
        throw error;
    }
};

export const getExpansionSets = async () => {
    try {
        console.log("Sprawdzam kolekcję sets...");
        const setsRef = collection(limitedTierlistDb, "sets");

        const querySnapshot = await getDocs(setsRef);
        console.log(`Kolekcja sets istnieje, znaleziono ${querySnapshot.docs.length} dokumentów`);

        const sets = querySnapshot.docs
            .map(setDoc => ({
                id: setDoc.id,
                ...setDoc.data()
            }))
            .filter(set => set.type === "EXPANSION" && (set.is_active !== false))
            .sort((a, b) => (a.sort_number || 0) - (b.sort_number || 0));

        console.log(`Znaleziono ${sets.length} setów typu EXPANSION:`, sets.map(s => ({ name: s.name, type: s.type, sort_number: s.sort_number })));
        return sets;
    } catch (error) {
        console.error("Błąd podczas pobierania setów EXPANSION:", error);
        console.log("Szczegóły błędu:", {
            message: error.message,
            code: error.code,
            stack: error.stack
        });

        if (error.code === 'permission-denied' || error.code === 'not-found') {
            console.log("Kolekcja sets nie istnieje lub brak uprawnień, zwracam pustą tablicę");
            return [];
        }

        throw error;
    }
};

export const getAllExpansionSetsForAdmin = async () => {
    try {
        console.log("Sprawdzam kolekcję sets dla admina...");
        const setsRef = collection(limitedTierlistDb, "sets");

        const querySnapshot = await getDocs(setsRef);
        console.log(`Kolekcja sets istnieje, znaleziono ${querySnapshot.docs.length} dokumentów`);

        const sets = querySnapshot.docs
            .map(setDoc => ({
                id: setDoc.id,
                ...setDoc.data()
            }))
            .filter(set => set.type === "EXPANSION")
            .sort((a, b) => (a.sort_number || 0) - (b.sort_number || 0));

        console.log(`Znaleziono ${sets.length} setów typu EXPANSION dla admina:`, sets.map(s => ({ name: s.name, type: s.type, sort_number: s.sort_number, is_active: s.is_active })));
        return sets;
    } catch (error) {
        console.error("Błąd podczas pobierania setów EXPANSION dla admina:", error);
        console.log("Szczegóły błędu:", {
            message: error.message,
            code: error.code,
            stack: error.stack
        });

        if (error.code === 'permission-denied' || error.code === 'not-found') {
            console.log("Kolekcja sets nie istnieje lub brak uprawnień, zwracam pustą tablicę");
            return [];
        }

        throw error;
    }
};

export const getAllLimitedCards = async () => {
    try {
        const cardsRef = collection(limitedTierlistDb, "cards");
        const q = query(cardsRef, orderBy("sort_number"));
        const querySnapshot = await getDocs(q);

        const cards = querySnapshot.docs.map(cardDoc => ({
            id: cardDoc.id,
            ...cardDoc.data()
        }));

        return cards;
    } catch (error) {
        console.error("Błąd podczas pobierania kart z tierlisty limited:", error);
        throw error;
    }
};

export const getLimitedCardsWithSets = async () => {
    try {
        console.log("Rozpoczynam pobieranie kart z setami...");

        const [cards, expansionSets] = await Promise.all([
            getAllLimitedCards(),
            getExpansionSets()
        ]);

        console.log(`Pobrano ${cards.length} kart i ${expansionSets.length} setów EXPANSION`);

        if (expansionSets.length === 0) {
            console.log("Brak setów EXPANSION, zwracam wszystkie karty bez setów");
            return cards.map(card => ({
                ...card,
                set: null
            }));
        }

        const setMap = {};
        expansionSets.forEach(set => {
            setMap[set.id] = set;
        });

        const allSets = await getAllSets();
        const allSetsMap = {};
        allSets.forEach(set => {
            allSetsMap[set.id] = set;
        });

        console.log("Mapa setów EXPANSION:", setMap);
        console.log("Mapa wszystkich setów:", allSetsMap);
        console.log("Przykład karty:", cards[0] ? { name: cards[0].name, card_sets: cards[0].card_sets } : 'brak kart');

        const filteredCards = cards
            .filter(card => {
                if (card.rarity === 'ENCHANTED' || card.rarity === 'SPECIAL') {
                    console.log(`Karta ${card.name} wykluczona - rarity: ${card.rarity}`);
                    return false;
                }

                if (card.card_identifier) {
                    const parts = card.card_identifier.split('/');
                    if (parts.length === 2) {
                        const firstNumber = parseInt(parts[0], 10);
                        const secondNumber = parseInt(parts[1], 10);

                        if (!isNaN(firstNumber) && !isNaN(secondNumber) && firstNumber > secondNumber) {
                            console.log(`Karta ${card.name} wykluczona - card_identifier: ${card.card_identifier} (${firstNumber} > ${secondNumber})`);
                            return false;
                        }
                    }
                }

                const hasExpansionSet = card.card_sets && card.card_sets.some(setId => setMap[setId]);
                if (!hasExpansionSet) {
                    console.log(`Karta ${card.name} (card_sets: ${card.card_sets}) nie ma pasującego setu EXPANSION`);
                } else {
                    console.log(`Karta ${card.name} (card_sets: ${card.card_sets}) ma set EXPANSION`);
                }
                return hasExpansionSet;
            })
            .map(card => {
                const expansionSetId = card.card_sets.find(setId => setMap[setId]);
                console.log(`Karta ${card.name}: expansionSetId = ${expansionSetId}, set = ${setMap[expansionSetId]?.name}`);

                const sortedCardSets = card.card_sets.sort((a, b) => {
                    const setA = allSetsMap[a] || { sort_number: 0 };
                    const setB = allSetsMap[b] || { sort_number: 0 };
                    return setA.sort_number - setB.sort_number;
                });

                return {
                    ...card,
                    set: setMap[expansionSetId],
                    card_sets: sortedCardSets
                };
            });

        console.log(`Przefiltrowano do ${filteredCards.length} kart z setów EXPANSION`);

        const sortedFilteredCards = filteredCards.sort((a, b) => {
            const aSortNumber = a.sort_number || 0;
            const bSortNumber = b.sort_number || 0;
            return aSortNumber - bSortNumber;
        });

        return sortedFilteredCards;
    } catch (error) {
        console.error("Błąd podczas łączenia kart z setami:", error);
        console.log("Używam fallback - pobieram wszystkie karty bez setów");

        try {
            const cards = await getAllLimitedCards();
            return cards.map(card => ({
                ...card,
                set: null
            }));
        } catch (fallbackError) {
            console.error("Błąd podczas fallback:", fallbackError);
            throw fallbackError;
        }
    }
};

export const getLimitedCard = async (cardId) => {
    try {
        const cardRef = doc(limitedTierlistDb, "cards", cardId);
        const cardDoc = await getDoc(cardRef);

        if (cardDoc.exists()) {
            return { id: cardDoc.id, ...cardDoc.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Błąd podczas pobierania karty:", error);
        throw error;
    }
};

export const updateLimitedCardRating = async (cardId, ratingData) => {
    try {
        const cardRef = doc(limitedTierlistDb, "cards", cardId);
        const updateData = {
            ...ratingData,
            modifiedAt: new Date().toISOString()
        };
        await updateDoc(cardRef, updateData);
        return cardId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji oceny karty:", error);
        throw error;
    }
};

export const updateLimitedCard = async (cardId, updateData) => {
    try {
        const cardRef = doc(limitedTierlistDb, "cards", cardId);
        const updateDataWithTimestamp = {
            ...updateData,
            modifiedAt: new Date().toISOString()
        };
        await updateDoc(cardRef, updateDataWithTimestamp);
        console.log(`Pomyślnie zaktualizowano kartę ${cardId}`);
        return cardId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji karty:", error);
        throw error;
    }
};

export const getAllLimitedCardsForAdmin = async () => {
    try {
        const cardsRef = collection(limitedTierlistDb, "cards");
        const q = query(cardsRef, orderBy("sort_number"));
        const querySnapshot = await getDocs(q);

        const cards = querySnapshot.docs.map(cardDoc => ({
            id: cardDoc.id,
            ...cardDoc.data()
        }));

        return cards;
    } catch (error) {
        console.error("Błąd podczas pobierania kart do edycji:", error);
        throw error;
    }
};

export const getAllLimitedCardsForAdminWithSets = async () => {
    try {
        console.log("Rozpoczynam pobieranie kart admina z setami...");

        const [cards, expansionSets] = await Promise.all([
            getAllLimitedCards(),
            getAllExpansionSetsForAdmin()
        ]);

        console.log(`Pobrano ${cards.length} kart i ${expansionSets.length} setów EXPANSION dla admina`);

        if (expansionSets.length === 0) {
            console.log("Brak setów EXPANSION dla admina, zwracam wszystkie karty bez setów");
            return cards.map(card => ({
                ...card,
                set: null
            }));
        }

        const setMap = {};
        expansionSets.forEach(set => {
            setMap[set.id] = set;
        });

        const allSets = await getAllSets();
        const allSetsMap = {};
        allSets.forEach(set => {
            allSetsMap[set.id] = set;
        });

        console.log("Mapa setów EXPANSION dla admina:", setMap);
        console.log("Mapa wszystkich setów dla admina:", allSetsMap);
        console.log("Przykład karty admina:", cards[0] ? { name: cards[0].name, card_sets: cards[0].card_sets } : 'brak kart');

        const filteredCards = cards
            .filter(card => {
                if (card.rarity === 'ENCHANTED' || card.rarity === 'SPECIAL') {
                    console.log(`Karta admina ${card.name} wykluczona - rarity: ${card.rarity}`);
                    return false;
                }

                const hasExpansionSet = card.card_sets && card.card_sets.some(setId => setMap[setId]);
                if (!hasExpansionSet) {
                    console.log(`Karta admina ${card.name} (card_sets: ${card.card_sets}) nie ma pasującego setu EXPANSION`);
                } else {
                    console.log(`Karta admina ${card.name} (card_sets: ${card.card_sets}) ma set EXPANSION`);
                }
                return hasExpansionSet;
            })
            .map(card => {
                const expansionSetId = card.card_sets.find(setId => setMap[setId]);
                console.log(`Karta admina ${card.name}: expansionSetId = ${expansionSetId}, set = ${setMap[expansionSetId]?.name}`);

                const sortedCardSets = card.card_sets.sort((a, b) => {
                    const setA = allSetsMap[a] || { sort_number: 0 };
                    const setB = allSetsMap[b] || { sort_number: 0 };
                    return setA.sort_number - setB.sort_number;
                });

                return {
                    ...card,
                    set: setMap[expansionSetId],
                    card_sets: sortedCardSets
                };
            });

        console.log(`Przefiltrowano do ${filteredCards.length} kart admina z setów EXPANSION`);

        const sortedFilteredCards = filteredCards.sort((a, b) => {
            const aSortNumber = a.sort_number || 0;
            const bSortNumber = b.sort_number || 0;
            return aSortNumber - bSortNumber;
        });

        return sortedFilteredCards;
    } catch (error) {
        console.error("Błąd podczas pobierania kart admina z setami:", error);
        console.log("Używam fallback - pobieram wszystkie karty admina bez setów");

        try {
            const cards = await getAllLimitedCards();
            return cards.map(card => ({
                ...card,
                set: null
            }));
        } catch (fallbackError) {
            console.error("Błąd podczas fallback admina:", fallbackError);
            throw fallbackError;
        }
    }
};
